import { cloneDeep } from 'lodash';

export const OUTPAINT_DIMENSION_PRESETS = [
  { label: 'Dimensions', value: null, icon: 'mdi-image-size-select-actual' },
  { label: 'Square', value: '1:1', icon: 'mdi-square-outline' },
  { label: '16:9', value: '16:9', icon: 'mdi-rectangle-outline' },
  { label: '9:16', value: '9:16', icon: 'mdi-cellphone' },
  { label: '4:3', value: '4:3', icon: 'mdi-crop-landscape' },
  { label: '3:4', value: '3:4', icon: 'mdi-crop-portrait' },
  { label: 'Custom', value: 'custom', icon: 'mdi-crop-free' }
];

const getDefaultState = () => ({
  mode: 'config',
  isLoading: false,
  originalImage: null,
  originalWidth: 0,
  originalHeight: 0,
  resultImage: null,
  width: 0,
  height: 0,
  aspectRatio: null,
  placement: {
    left: 0,
    top: 0,
    scaleX: 1,
    scaleY: 1
  },
  history: [],
  historyIndex: -1,
  resultWidth: 0,
  resultHeight: 0
});

const state = getDefaultState();

const getters = {
  outpaintMode: state => state.mode,
  isOutpaintLoading: state => state.isLoading,
  outpaintOriginalImage: state => state.originalImage,
  outpaintResultImage: state => state.resultImage,
  outpaintConfig: state => ({
    width: state.width,
    height: state.height,
    aspectRatio: state.aspectRatio
  }),
  outpaintPlacement: state => state.placement,
  outpaintDimensionPresets: () => OUTPAINT_DIMENSION_PRESETS,
  canUndoOutpaintConfig: state => state.historyIndex > 0,
  canRedoOutpaintConfig: state => state.historyIndex < state.history.length - 1,
  hasOutpaintHistory: state => state.history.length > 1,
  originalImageWidth: state => state.originalWidth,
  originalImageHeight: state => state.originalHeight,
  outpaintResultWidth: state => state.resultWidth,
  outpaintResultHeight: state => state.resultHeight
};

const mutations = {
  setOutpaintMode(state, mode) {
    state.mode = mode;
  },
  setOutpaintLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
  setOutpaintOriginalImage(state, { url, width, height }) {
    state.originalImage = url;
    state.originalWidth = width || 0;
    state.originalHeight = height || 0;
  },
  setOutpaintResultImage(state, { url, width, height }) {
    state.resultImage = url;
    state.resultWidth = width || 0;
    state.resultHeight = height || 0;
  },
  updateOutpaintConfig(state, payload) {
    if (payload.width !== undefined) state.width = payload.width;
    if (payload.height !== undefined) state.height = payload.height;
    if (payload.aspectRatio !== undefined) state.aspectRatio = payload.aspectRatio;
  },
  updateOutpaintPlacement(state, payload) {
    if (payload.left !== undefined) state.placement.left = payload.left;
    if (payload.top !== undefined) state.placement.top = payload.top;
    if (payload.scaleX !== undefined) state.placement.scaleX = payload.scaleX;
    if (payload.scaleY !== undefined) state.placement.scaleY = payload.scaleY;
  },
  pushOutpaintHistory(state) {
    if (state.historyIndex < state.history.length - 1) {
      state.history = state.history.slice(0, state.historyIndex + 1);
    }
    const snapshot = {
      width: state.width,
      height: state.height,
      aspectRatio: state.aspectRatio,
      placement: cloneDeep(state.placement),
      mode: 'config',
      resultImage: state.mode === 'result' ? state.resultImage : null,
      resultWidth: state.mode === 'result' ? state.resultWidth : 0,
      resultHeight: state.mode === 'result' ? state.resultHeight : 0
    };
    state.history.push(snapshot);
    state.historyIndex = state.history.length - 1;
    if (state.history.length > 50) {
      state.history.shift();
      state.historyIndex--;
    }
  },
  pushOutpaintResultToHistory(state) {
    if (state.historyIndex < state.history.length - 1) {
      state.history = state.history.slice(0, state.historyIndex + 1);
    }
    const snapshot = {
      width: state.width,
      height: state.height,
      aspectRatio: state.aspectRatio,
      placement: cloneDeep(state.placement),
      mode: 'result',
      resultImage: state.resultImage,
      resultWidth: state.resultWidth,
      resultHeight: state.resultHeight
    };
    state.history.push(snapshot);
    state.historyIndex = state.history.length - 1;
    if (state.history.length > 50) {
      state.history.shift();
      state.historyIndex--;
    }
  },
  undoOutpaintConfig(state) {
    if (state.historyIndex > 0) {
      state.historyIndex--;
      applySnapshot(state, state.history[state.historyIndex]);
    }
  },
  redoOutpaintConfig(state) {
    if (state.historyIndex < state.history.length - 1) {
      state.historyIndex++;
      applySnapshot(state, state.history[state.historyIndex]);
    }
  },
  resetOutpaintState(state) {
    Object.assign(state, getDefaultState());
  },
  clearOutpaintHistory(state) {
    state.history = [];
    state.historyIndex = -1;
  }
};

function applySnapshot(state, snapshot) {
  state.width = snapshot.width;
  state.height = snapshot.height;
  state.aspectRatio = snapshot.aspectRatio;
  state.placement = cloneDeep(snapshot.placement);
  state.mode = snapshot.mode ?? 'config';
  state.resultImage = snapshot.resultImage ?? null;
  state.resultWidth = snapshot.resultWidth ?? 0;
  state.resultHeight = snapshot.resultHeight ?? 0;
}

const actions = {
  initializeOutpaint({ commit }, { url, width, height }) {
    commit('resetOutpaintState');
    commit('setOutpaintOriginalImage', { url, width, height });
  },

  setOutpaintConfig({ commit }, config) {
    commit('updateOutpaintConfig', config);
    commit('pushOutpaintHistory');
  },

  updateOutpaintConfigOnly({ commit }, config) {
    commit('updateOutpaintConfig', config);
  },

  setOutpaintPlacement({ commit }, placement) {
    commit('updateOutpaintPlacement', placement);
    commit('pushOutpaintHistory');
  },

  updateOutpaintPlacementOnly({ commit }, placement) {
    commit('updateOutpaintPlacement', placement);
  },

  setOutpaintConfigWithPlacement({ commit }, { config, placement }) {
    commit('updateOutpaintConfig', config);
    commit('updateOutpaintPlacement', placement);
    commit('pushOutpaintHistory');
  },

  undoOutpaintConfig({ commit }) {
    commit('undoOutpaintConfig');
  },
  redoOutpaintConfig({ commit }) {
    commit('redoOutpaintConfig');
  },
  setOutpaintMode({ commit }, mode) {
    commit('setOutpaintMode', mode);
  },
  setOutpaintLoading({ commit }, isLoading) {
    commit('setOutpaintLoading', isLoading);
  },
  setOutpaintResultImage({ commit }, payload) {
    commit('setOutpaintResultImage', payload);
  },
  pushOutpaintHistory({ commit }) {
    commit('pushOutpaintHistory');
  },
  pushOutpaintResultToHistory({ commit }) {
    commit('pushOutpaintResultToHistory');
  },
  resetOutpaintState({ commit }) {
    commit('resetOutpaintState');
  },
  clearOutpaintHistory({ commit }) {
    commit('clearOutpaintHistory');
  }
};

export const outpaint = {
  namespaced: false,
  state,
  getters,
  mutations,
  actions
};
